import { useState } from 'react'
import { getApiUrl, fetchWithAuth } from '@/api'

interface MemberInfo {
  id: number
  login_id: string
  name: string
  total_points: number
}

export default function AdminPointAdjust() {
  const [loginIdInput, setLoginIdInput] = useState('')
  const [memberInfo, setMemberInfo] = useState<MemberInfo | null>(null)
  const [memberLoading, setMemberLoading] = useState(false)
  const [memberError, setMemberError] = useState('')

  const [type, setType] = useState<'1' | '2'>('1')
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)

  // 결과 모달
  const [resultModal, setResultModal] = useState<{
    memberName: string
    loginId: string
    type: '1' | '2'
    amount: number
    balanceBefore: number
    balanceAfter: number
  } | null>(null)

  const handleMemberSearch = async () => {
    if (!loginIdInput.trim()) return
    setMemberLoading(true)
    setMemberError('')
    setMemberInfo(null)
    try {
      const res = await fetchWithAuth(
        getApiUrl(`/marubit/api/member/admin/search?login_id=${encodeURIComponent(loginIdInput.trim())}&limit=1`)
      )
      if (!res.ok) throw new Error('조회 실패')
      const data = await res.json()
      const found: MemberInfo | null = data.members?.[0] ?? null
      if (!found) {
        setMemberError('해당 아이디의 회원을 찾을 수 없습니다.')
      } else {
        setMemberInfo(found)
      }
    } catch {
      setMemberError('회원 조회에 실패했습니다.')
    } finally {
      setMemberLoading(false)
    }
  }

  const handleMemberKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleMemberSearch()
  }

  const handleSubmit = async () => {
    if (!memberInfo) return
    if (!amount || Number(amount) < 1) { alert('포인트를 1 이상 입력해주세요.'); return }
    if (!description.trim()) { alert('내용을 입력해주세요.'); return }

    const amountNum = Number(amount)
    const action = type === '1' ? '적립' : '차감'
    if (!window.confirm(`${memberInfo.login_id}(${memberInfo.name})의 포인트를 ${amountNum.toLocaleString()}P ${action}하시겠습니까?`)) return

    setSaving(true)
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/point/admin/adjust'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          member_id: memberInfo.id,
          type: Number(type),
          amount: amountNum,
          description: description.trim(),
        }),
      })
      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.detail?.message ?? err.detail ?? '처리 실패')
      }
      const result = await res.json()

      const balanceBefore = memberInfo.total_points
      setResultModal({
        memberName: memberInfo.name,
        loginId: memberInfo.login_id,
        type,
        amount: amountNum,
        balanceBefore,
        balanceAfter: result.balance_after,
      })

      // 폼 초기화
      setMemberInfo(prev => prev ? { ...prev, total_points: result.balance_after } : null)
      setAmount('')
      setDescription('')
      setType('1')
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '처리에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="bg-[#f5f2f0] min-h-screen p-8">
      <div className="max-w-[680px] mx-auto">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">포인트 증감 설정</h1>
          <p className="text-[13px] text-gray-400">
            {new Date().toLocaleDateString('ko-KR', {
              year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
            })}
          </p>
        </div>

        {/* Step 1: 회원 조회 */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] px-6 py-6 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="text-[14px] font-bold text-[#5a3d32] mb-4">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#887267] text-white text-[11px] font-bold mr-2">1</span>
            회원 조회
          </h2>
          <div className="flex gap-2 items-end">
            <div className="flex flex-col gap-1 flex-1">
              <label className="text-[11px] text-[#5a3d32] font-medium">회원 아이디</label>
              <input
                type="text"
                value={loginIdInput}
                onChange={e => setLoginIdInput(e.target.value)}
                onKeyDown={handleMemberKeyDown}
                placeholder="아이디를 입력하세요"
                className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] transition-colors"
              />
            </div>
            <button
              onClick={handleMemberSearch}
              disabled={memberLoading || !loginIdInput.trim()}
              className="bg-[#887267] text-white text-[13px] font-semibold px-4 py-2 rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50 disabled:cursor-not-allowed h-[38px] whitespace-nowrap"
            >
              {memberLoading ? '조회 중...' : '조회'}
            </button>
          </div>

          {memberError && (
            <p className="text-[12px] text-red-400 mt-2">{memberError}</p>
          )}

          {memberInfo && (
            <div className="mt-4 bg-[#fdf8f0] rounded-xl border border-[#e8d8ce] px-4 py-3 flex items-center justify-between">
              <div>
                <div className="text-[13px] font-bold text-[#2a1f1a]">
                  {memberInfo.name}
                  <span className="text-[12px] text-gray-400 font-normal ml-2">({memberInfo.login_id})</span>
                </div>
                <div className="text-[12px] text-gray-500 mt-0.5">회원 ID: {memberInfo.id}</div>
              </div>
              <div className="text-right">
                <div className="text-[11px] text-gray-400">현재 포인트</div>
                <div className="text-[18px] font-extrabold text-[#887267]">
                  {memberInfo.total_points.toLocaleString()}P
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Step 2: 증감 설정 */}
        <div className={`bg-white rounded-2xl border border-[#e8e0dc] px-6 py-6 shadow-[0_2px_12px_rgba(136,114,103,0.06)] transition-opacity ${
          memberInfo ? 'opacity-100' : 'opacity-40 pointer-events-none'
        }`}>
          <h2 className="text-[14px] font-bold text-[#5a3d32] mb-5">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#887267] text-white text-[11px] font-bold mr-2">2</span>
            증감 설정
          </h2>

          <div className="flex flex-col gap-5">
            {/* 구분 */}
            <div className="flex flex-col gap-2">
              <label className="text-[12px] text-[#5a3d32] font-medium">구분</label>
              <div className="flex gap-3">
                {(['1', '2'] as const).map(v => (
                  <button
                    key={v}
                    onClick={() => setType(v)}
                    className={`flex-1 py-2.5 rounded-xl text-[13px] font-semibold border-2 transition-colors cursor-pointer ${
                      type === v
                        ? v === '1'
                          ? 'bg-[#e8f5e9] border-[#388e3c] text-[#388e3c]'
                          : 'bg-[#fce4ec] border-[#c62828] text-[#c62828]'
                        : 'bg-white border-[#e8e0dc] text-gray-400 hover:border-[#887267]'
                    }`}
                  >
                    {v === '1' ? '✦ 적립' : '▾ 차감'}
                  </button>
                ))}
              </div>
            </div>

            {/* 포인트 */}
            <div className="flex flex-col gap-1">
              <label className="text-[12px] text-[#5a3d32] font-medium">포인트</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="예: 500"
                  className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] w-full transition-colors"
                />
                <span className="text-[13px] text-gray-400 whitespace-nowrap">P</span>
              </div>
              {memberInfo && amount && Number(amount) > 0 && (
                <p className="text-[11px] text-gray-400 mt-1">
                  처리 후 예상 포인트:{' '}
                  <span className="text-[#887267] font-semibold">
                    {type === '1'
                      ? (memberInfo.total_points + Number(amount)).toLocaleString()
                      : (memberInfo.total_points - Number(amount)).toLocaleString()}P
                  </span>
                  {type === '2' && memberInfo.total_points - Number(amount) < 0 && (
                    <span className="text-red-400 ml-2">⚠ 포인트 부족</span>
                  )}
                </p>
              )}
            </div>

            {/* 내용 */}
            <div className="flex flex-col gap-1">
              <label className="text-[12px] text-[#5a3d32] font-medium">내용</label>
              <input
                type="text"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="예: 이벤트 참여 보상"
                maxLength={200}
                className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] transition-colors"
              />
              <div className="text-right text-[11px] text-gray-300">{description.length}/200</div>
            </div>
          </div>

          <div className="border-t border-[#f5f2f0] pt-5 flex justify-end mt-2">
            <button
              onClick={handleSubmit}
              disabled={saving || !memberInfo || !amount || !description.trim() || Number(amount) < 1}
              className="bg-[#887267] text-white text-[13px] font-semibold px-8 py-2.5 rounded-lg cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-none"
            >
              {saving ? '처리 중...' : '확인'}
            </button>
          </div>
        </div>

      </div>

      {/* 결과 모달 */}
      {resultModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl px-8 py-7 max-w-[380px] w-full mx-4">
            <div className="flex items-center justify-center mb-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${
                resultModal.type === '1' ? 'bg-[#e8f5e9]' : 'bg-[#fce4ec]'
              }`}>
                {resultModal.type === '1' ? '✦' : '▾'}
              </div>
            </div>
            <h3 className="text-[16px] font-extrabold text-[#2a1f1a] text-center mb-1">
              포인트 {resultModal.type === '1' ? '적립' : '차감'} 완료
            </h3>
            <p className="text-[13px] text-gray-400 text-center mb-5">
              {resultModal.loginId} ({resultModal.memberName})
            </p>

            <div className="bg-[#f5f2f0] rounded-xl px-5 py-4 flex flex-col gap-2 mb-6">
              <div className="flex justify-between text-[13px]">
                <span className="text-gray-500">변경 전</span>
                <span className="font-semibold text-[#2a1f1a]">{resultModal.balanceBefore.toLocaleString()}P</span>
              </div>
              <div className="flex justify-between text-[13px]">
                <span className="text-gray-500">{resultModal.type === '1' ? '적립' : '차감'} 포인트</span>
                <span className={`font-bold ${resultModal.type === '1' ? 'text-[#388e3c]' : 'text-[#c62828]'}`}>
                  {resultModal.type === '1' ? '+' : '-'}{resultModal.amount.toLocaleString()}P
                </span>
              </div>
              <div className="border-t border-[#e8e0dc] pt-2 flex justify-between text-[14px]">
                <span className="text-[#5a3d32] font-semibold">변경 후</span>
                <span className="font-extrabold text-[#887267]">{resultModal.balanceAfter.toLocaleString()}P</span>
              </div>
            </div>

            <button
              onClick={() => setResultModal(null)}
              className="w-full bg-[#887267] text-white text-[14px] font-semibold py-3 rounded-xl border-none cursor-pointer hover:bg-[#6b5248] transition-colors"
            >
              확인
            </button>
          </div>
        </div>
      )}
    </div>
  )
}